import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, ArrowRight } from 'lucide-react';
import { PLATFORMS } from '../data/content';
import SEO from '../components/SEO';

const PLATFORM_SEO = {
  brontoai: {
    title: 'BrontoAI — A Knowledge Intelligence Platform by Bronto Platforms',
    description: 'BrontoAI is an artificial intelligence platform by Bronto Platforms Private Limited, designed for assistive intelligence, knowledge synthesis, and conversational discovery.',
    path: '/brontoai',
  },
  brontoy: {
    title: 'BrontoY — A Social Platform by Bronto Platforms',
    description: 'BrontoY is a warm social space by Bronto Platforms for sharing moments, expressing opinions, and discovering different perspectives through thoughtful, focused conversations.',
    path: '/brontoy',
  },
  mspconnect: {
    title: 'MSPConnect — Agricultural Intelligence by Bronto Platforms',
    description: 'MSPConnect is an agricultural intelligence platform by Bronto Platforms, surfacing Minimum Support Price data and market signals for India\'s farming ecosystem.',
    path: '/mspconnect',
  },
  brontocosmos: {
    title: 'Bronto Cosmos — Space and Astronomy Discovery Platform',
    description: 'Bronto Cosmos is a space, science, and astronomy discovery platform by Bronto Platforms — explore the universe through real cosmic data and beautiful discovery experiences.',
    path: '/brontocosmos',
  },
};

const PlatformDetailPage = ({ platformId }) => {
  const platform = PLATFORMS.find((p) => p.id === platformId);
  const otherPlatform = PLATFORMS.find((p) => p.id !== platformId);

  if (!platform) return null;

  const seoMeta = PLATFORM_SEO[platformId] || {
    title: platform.name,
    description: platform.shortDescription || `${platform.name} by Bronto Platforms`,
    path: `/${platformId}`,
  };

  return (
    <div className="bg-[#050505] text-white" data-testid={`${platformId}-page`}>
      <SEO title={seoMeta.title} description={seoMeta.description} path={seoMeta.path} />
      {/* Hero */}
      <section className="relative pt-12 pb-24 px-6 lg:px-10 overflow-hidden">
        <div
          className={`absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full opacity-25 blur-3xl bg-gradient-to-br ${platform.gradient}`}
        />
        <div className="relative z-10 max-w-7xl mx-auto">
          <Link
            to="/platforms"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white mb-12 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            All Platforms
          </Link>

          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-6">
            {platform.category}
          </div>
          <h1
            className={`text-display text-6xl sm:text-7xl md:text-8xl lg:text-9xl mb-10 bg-gradient-to-r ${platform.gradient} bg-clip-text text-transparent`}
          >
            {platform.name}
          </h1>
          <p className="text-xl md:text-2xl text-white/65 max-w-3xl leading-relaxed font-light mb-12">
            {platform.shortDescription}
          </p>

          <div className="flex flex-wrap items-center gap-4">
            <a
              href={platform.appStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              data-testid={`${platformId}-app-store-btn`}
            >
              {platform.buttonText}
              <ExternalLink className="h-4 w-4" />
            </a>
            <Link to="/contact" className="btn-secondary">
              Contact
            </Link>
          </div>
        </div>
      </section>

      {/* About the Platform */}
      <section className="py-24 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-3">
              About
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              The platform.
            </h2>
          </div>
          <div className="md:col-span-8">
            <p className="text-lg md:text-xl text-white/80 leading-relaxed">
              {platform.description}
            </p>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-3">
              Capabilities
            </div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight max-w-3xl">
              What {platform.name} provides.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 rounded-3xl overflow-hidden">
            {platform.capabilities.map((cap, idx) => (
              <div key={cap} className="bg-[#050505] p-8 md:p-10 hover:bg-white/[0.03] transition-colors">
                <div className="text-xs font-semibold text-white/30 mb-3">0{idx + 1}</div>
                <h3 className="text-xl font-bold tracking-tight">{cap}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="relative rounded-3xl glass-strong p-12 md:p-16 text-center overflow-hidden">
            <div
              className={`absolute -top-40 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full blur-3xl opacity-20 bg-gradient-to-br ${platform.gradient}`}
            />
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                Get {platform.name}.
              </h2>
              <p className="text-base md:text-lg text-white/60 max-w-2xl mx-auto mb-10">
                {platform.id === 'brontocosmos' ? 'Available.' : 'Available on the App Store.'}
              </p>
              <a
                href={platform.appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex"
              >
                {platform.buttonText}
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Next Platform */}
      {otherPlatform && (
        <section className="py-24 px-6 lg:px-10 border-t border-white/5">
          <div className="max-w-5xl mx-auto">
            <Link
              to={`/${otherPlatform.id}`}
              className="group block rounded-3xl glass hover-lift p-10 md:p-14 relative overflow-hidden"
            >
              <div
                className={`absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-15 blur-3xl bg-gradient-to-br ${otherPlatform.gradient}`}
              />
              <div className="relative z-10">
                <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-3">
                  Next Platform
                </div>
                <div className="flex items-center justify-between gap-6">
                  <h3
                    className={`text-4xl md:text-5xl font-bold tracking-tight bg-gradient-to-r ${otherPlatform.gradient} bg-clip-text text-transparent`}
                  >
                    {otherPlatform.name}
                  </h3>
                  <div className="w-14 h-14 rounded-full bg-white/10 group-hover:bg-white group-hover:text-black flex items-center justify-center transition-all shrink-0">
                    <ArrowRight className="h-5 w-5" />
                  </div>
                </div>
                <p className="text-base text-white/65 mt-4 max-w-2xl">
                  {otherPlatform.shortDescription}
                </p>
              </div>
            </Link>
          </div>
        </section>
      )}
    </div>
  );
};

export default PlatformDetailPage;
